import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import apiFetch from "../../../context/api";
import useMediaQuery from "../../hooks/useMediaQuery";

const PIE_COLORS = [
  "#84cc16","#0ea5e9","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#f97316",
  "#a3e635","#7c3aed","#0284c7","#d97706","#4f46e5","#dc2626","#059669",
  "#ca8a04","#2563eb","#16a34a","#c2410c","#6d28d9","#0369a1",
];

/** Móvil + tablet vertical: gráfico centrado con leyenda debajo */
// Compacto (tarta arriba, leyenda en píldoras debajo) para TODO lo menor que xl:
// en tablet apaisada el modo escritorio ponía la leyenda vertical (22+ ítems) al
// lado de una tarta de h-64 y se desbordaba del panel pisando lo de alrededor.
const PIE_COMPACT_LAYOUT_MQ = "(max-width: 1279px)";
const PIE_MOBILE_MQ = "(max-width: 767px)";

function fmt(n, decimals = 2) {
  return Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-2 text-xs">
      <div className="font-semibold text-gray-800">{name}</div>
      <div className="text-gray-500">{fmt(value)} {p.unit}</div>
    </div>
  );
}

function buildPieData(items, metric) {
  return items
    .map((p) => ({
      name: p.name,
      value: metric === "valor"
        ? Number(((p.stock || 0) * (p.price || 0)).toFixed(2))
        : Number((p.stock || 0).toFixed(3)),
      unit: metric === "valor" ? "€" : "uds",
    }))
    .filter((p) => p.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);
}

function buildCategories(items, metric) {
  const map = {};
  items.forEach((p) => {
    const k = p.category || "Sin categoría";
    if (!map[k]) map[k] = { name: k, stock: 0, value: 0 };
    map[k].stock += p.stock;
    map[k].value += (p.stock || 0) * (p.price || 0);
  });
  return Object.values(map)
    .sort((a, b) => (metric === "valor" ? b.value - a.value : b.stock - a.stock));
}

function renderDesktopPieLabel({ percent }) {
  if (!percent || percent <= 0.04) return null;
  return `${(percent * 100).toFixed(1)}%`;
}

function CompactPieLegend({ payload }) {
  if (!payload?.length) return null;
  return (
    <ul className="m-0 flex w-full list-none flex-wrap justify-center gap-x-4 gap-y-1.5 p-0 text-center">
      {payload.map((entry) => (
        <li key={`${entry.value}-${entry.color}`} className="inline-flex items-center justify-center gap-1.5">
          <span
            className="inline-block shrink-0 rounded-sm"
            style={{ width: 10, height: 10, backgroundColor: entry.color }}
            aria-hidden
          />
          <span className="text-[11px] text-gray-500">
            {entry.value?.length > 24 ? `${entry.value.slice(0, 24)}…` : entry.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CompactPiePercentLabel(props) {
  const { cx, cy, midAngle, outerRadius, percent } = props;
  if (!percent || percent <= 0.04) return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(midAngle)) return null;

  const RADIAN = Math.PI / 180;
  const radius = (outerRadius || 0) + 22;
  const lx = cx + radius * Math.cos(-midAngle * RADIAN);
  const ly = cy + radius * Math.sin(-midAngle * RADIAN);
  const textAnchor = lx > cx + 2 ? "start" : lx < cx - 2 ? "end" : "middle";

  return (
    <text
      x={lx}
      y={ly}
      fill="#374151"
      textAnchor={textAnchor}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
      pointerEvents="none"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
}

function PiePanel({ title, data }) {
  const isCompactPie = useMediaQuery(PIE_COMPACT_LAYOUT_MQ);
  const isMobilePie = useMediaQuery(PIE_MOBILE_MQ);

  const compactOuterRadius = isMobilePie ? "92%" : "90%";
  const compactChartHeight = isMobilePie ? "h-[460px]" : "h-[480px]";
  const compactMargin = isMobilePie
    ? { top: 18, right: 20, left: 20, bottom: 4 }
    : { top: 20, right: 24, left: 24, bottom: 4 };
  const compactCy = isMobilePie ? "40%" : "39%";

  if (!data.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-center">
        <span className="text-sm text-gray-400">Sin datos</span>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm max-md:px-2 max-lg:portrait:px-2 max-lg:portrait:py-4">
      <div className="mb-2 text-sm font-semibold text-gray-700 max-lg:portrait:text-center">{title}</div>
      <div className={isCompactPie ? `${compactChartHeight} w-full` : "h-64"}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            style={isCompactPie ? { overflow: "visible" } : undefined}
            margin={isCompactPie ? compactMargin : undefined}
          >
            <Pie
              key={isCompactPie ? (isMobilePie ? "mobile" : "tablet-portrait") : "desktop"}
              data={data}
              cx={isCompactPie ? "50%" : "38%"}
              cy={isCompactPie ? compactCy : "50%"}
              outerRadius={isCompactPie ? compactOuterRadius : 90}
              paddingAngle={isCompactPie ? 1.5 : 0}
              dataKey="value"
              nameKey="name"
              labelLine={false}
              label={isCompactPie ? (props) => <CompactPiePercentLabel {...props} /> : renderDesktopPieLabel}
              isAnimationActive={false}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout={isCompactPie ? "horizontal" : "vertical"}
              align={isCompactPie ? "center" : "right"}
              verticalAlign={isCompactPie ? "bottom" : "middle"}
              iconSize={isCompactPie ? 10 : 8}
              wrapperStyle={
                isCompactPie
                  ? {
                      paddingTop: 10,
                      width: "100%",
                      left: 0,
                      right: 0,
                      lineHeight: 1.5,
                      display: "flex",
                      justifyContent: "center",
                    }
                  : // Escritorio: la leyenda vertical con muchas genéticas puede medir
                    // más que la tarta (h-64) — se acota y hace scroll en vez de
                    // desbordarse del panel.
                    { maxHeight: 240, overflowY: "auto", paddingRight: 4 }
              }
              content={isCompactPie ? (props) => <CompactPieLegend {...props} /> : undefined}
              formatter={
                isCompactPie
                  ? undefined
                  : (value) => (
                      <span style={{ fontSize: 10, color: "#6b7280" }}>
                        {value.length > 20 ? value.slice(0, 20) + "…" : value}
                      </span>
                    )
              }
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function StockSection({ group, label, unitLabel }) {
  const [metric, setMetric]   = useState("stock");  // stock | valor
  const [groupBy, setGroupBy] = useState("articulo"); // articulo | categoria

  const products      = useMemo(() => group?.products || [], [group]);
  const inStockCount  = useMemo(() => products.filter((p) => (p.stock || 0) > 0).length, [products]);
  const categories = useMemo(() => buildCategories(products, metric), [products, metric]);

  const byArticuloPie  = useMemo(() => buildPieData(products,   metric), [products, metric]);
  const byCategoriaPie = useMemo(() => buildPieData(categories, metric), [categories, metric]);

  const tableRows = groupBy === "categoria" ? categories : products;

  const totalStock = group?.total_stock || 0;
  const totalValue = group?.total_value || 0;
  const count      = group?.count || 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        {/* Metric toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[{ id: "stock", l: "Unidades" }, { id: "valor", l: "Valor €" }].map((t) => (
            <button
              key={t.id}
              onClick={() => setMetric(t.id)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                metric === t.id ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        {/* Group toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[{ id: "articulo", l: "Artículo" }, { id: "categoria", l: "Categoría" }].map((t) => (
            <button
              key={t.id}
              onClick={() => setGroupBy(t.id)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                groupBy === t.id ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        {/* Totals */}
        <div className="ml-auto flex flex-wrap gap-4 text-sm">
          <span className="text-gray-500">
            Total: <strong className="text-gray-800">{count}</strong>
            {inStockCount < count && (
              <span className="ml-1 text-[var(--success)]">({inStockCount} con stock)</span>
            )}
          </span>
          <span className="text-gray-500">
            Stock: <strong className="text-[var(--success)]">{fmt(totalStock, 3)} {unitLabel}</strong>
          </span>
          <span className="text-gray-500">
            Valor: <strong className="text-gray-800">{fmt(totalValue)} €</strong>
          </span>
        </div>
      </div>

      {/* Dual pie charts */}
      {products.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-gray-400">Sin productos registrados en {label}</p>
        </div>
      ) : (
        <div className="grid gap-4 max-lg:portrait:gap-6 xl:grid-cols-2">
          <PiePanel
            title={`Distribución ${metric === "valor" ? "valor" : "stock"} — por artículo`}
            data={byArticuloPie}
          />
          <PiePanel
            title={`Distribución ${metric === "valor" ? "valor" : "stock"} — por categoría`}
            data={byCategoriaPie}
          />
        </div>
      )}

      {/* Product / Category table */}
      {products.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex justify-between items-center">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {groupBy === "categoria" ? "Categoría" : "Artículo"}
            </span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {metric === "stock" ? `Stock (${unitLabel})` : "Valor (€)"}
            </span>
          </div>
          <div
            className="text-sm"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
          >
            {tableRows.map((item, i) => {
              const stockVal = item.stock || 0;
              const noStock  = stockVal <= 0;
              const displayVal = metric === "valor"
                ? `${fmt(stockVal * (item.price || 0))} €`
                : `${fmt(stockVal, item.unit_type === "weight" ? 3 : 2)} ${unitLabel}`;
              return (
                <div
                  key={i}
                  className={`flex justify-between items-center px-4 py-2 border-b border-r border-gray-100 transition-colors ${
                    noStock ? "opacity-50" : "hover:bg-gray-50"
                  }`}
                >
                  <span className="text-gray-700 truncate mr-2">{item.name}</span>
                  <span className={`font-semibold shrink-0 ${noStock ? "text-gray-400" : "text-[var(--success)]"}`}>
                    {displayVal}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ControlDeStock({ token, venueID }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [areaTab, setAreaTab] = useState("dispensario");
  // Fecha histórica (petición Kong, 14-ago): vacío = stock actual; con fecha, el
  // backend reconstruye el stock de ese día desde el ledger de movimientos.
  const [atDate, setAtDate]   = useState("");

  const fetchData = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      const fecha = atDate ? `&at_date=${atDate}` : "";
      const res = await apiFetch(`/v1/reports/stock-products?venue_id=${venueID}${fecha}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Error al cargar stock");
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, atDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const AREA_TABS = [
    { id: "dispensario", label: "Dispensario" },
    { id: "productos",   label: "Productos" },
  ];

  return (
    <div className="space-y-4">
      {/* Area tab selector */}
      <div className="bg-[var(--muted)] rounded-md p-1 flex gap-1">
        {AREA_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setAreaTab(t.id)}
            aria-pressed={areaTab === t.id}
            className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-sm whitespace-nowrap transition-colors ${
              areaTab === t.id
                ? "bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)]"
                : "border border-transparent text-[var(--muted-foreground)] hover:bg-[var(--secondary)] hover:text-[var(--foreground)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Stock a una fecha: elegir día = ver el stock que había entonces,
          reconstruido del ledger de movimientos. Vacío = stock actual. */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-500 font-medium">Stock a fecha</label>
        <input
          type="date"
          className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
          value={atDate}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setAtDate(e.target.value)}
        />
        {atDate && (
          <button
            type="button"
            onClick={() => setAtDate("")}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Volver a hoy
          </button>
        )}
        {data?.at_date && (
          <span className="rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-medium text-amber-700">
            Viendo stock del {data.at_date.split("-").reverse().join("/")} · valor a precios actuales
          </span>
        )}
      </div>

      {loading && (
        <div className="space-y-4">
          <div className="h-72 bg-gray-100 rounded-xl animate-pulse" />
          <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />
        </div>
      )}
      {error && (
        <div className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</div>
      )}

      {!loading && data && (
        <StockSection
          key={areaTab}
          group={data[areaTab]}
          label={areaTab === "dispensario" ? "Dispensario" : "Productos"}
          unitLabel={areaTab === "dispensario" ? "gr" : "uds"}
        />
      )}
    </div>
  );
}
